<template>
  <div class="geogrids-widget-container">
    <div v-show='loader' class='loader'><i class='fa fa-spinner fa-pulse fa-fw' /></div>
    <tabs />
    <newGeogrid
      :gmb-locations-path='gmbLocationsPath'
      :keys-path='keysPath'
      :distance-measure-options='distanceMeasureOptions'
      :places-api-keys='defaultPlacesApiKeys'
      :point-distances='pointDistances'
      :api-geogrids-path='apiGeogridsPath'
      :all-locations='allLocations'
      @waitGeogrid='waitGeogrid'
      @reloadHistory='reloadHistory'
      v-show="pageState === 'live' && pageTab === 'new'" />
    <geogridDetails
      :api-geogrids-path='apiGeogridsPath'
      @reloadHistory='reloadHistory'
      v-show="pageState === 'live' && pageTab === 'show'" />
    <geogridsHistory
      ref='history'
      :geogrids-path='geogridsPath'
      :api-geogrids-path='apiGeogridsPath'
      v-show="pageState === 'live' && pageTab === 'index'" />
    <scheduledGeogridsConfigs
      ref='configs'
      :api-configs-path='apiConfigsPath'
      :api-total-configs-path='apiTotalConfigsPath'
      v-if="pageState === 'scheduled' && pageTab ==='index'" />
    <newConfig
      :api-geogrids-path='apiGeogridsPath'
      :periodicity-options='periodicityOptions'
      :day-number-options='dayNumberOptions'
      :week-number-options='weekNumberOptions'
      :keys-path='keysPath'
      :distance-measure-options='distanceMeasureOptions'
      :places-api-keys='defaultPlacesApiKeys'
      :api-configs-path='apiConfigsPath'
      :gmb-locations-path='gmbLocationsPath'
      :all-locations='allLocations'
      @createdNewConfig='createdNewConfig'
      v-show="pageState === 'scheduled' && pageTab === 'new'" />
    <editConfig
      ref='editConfig'
      :periodicity-options='periodicityOptions'
      :day-number-options='dayNumberOptions'
      :week-number-options='weekNumberOptions'
      :new-key-path='newKeyPath'
      :keys-path='keysPath'
      :point-distances='pointDistances'
      :places-data-source-default-options='placesDataSourceDefaultOptions'
      :distance-measure-options='distanceMeasureOptions'
      :places-api-keys='defaultPlacesApiKeys'
      :api-configs-path='apiConfigsPath'
      @reloadHistory='reloadHistory'
      v-show="pageState === 'scheduled' && pageTab === 'edit'" />
    <div v-show="pageTab !== 'index'" :class="['configs-show gap-4', { 'big-map': isScheduledShow}]">
      <div v-show="isScheduledShow" class="config">
        <config />
        <searchTermsList />
      </div>
      <div class="configs-map d-flex flex-column gap-2">
        <geoAreaMap
          ref='map'
          :geogrids-path='geogridsPath'
          :point-distances='pointDistances'
          :distance-measure-options='distanceMeasureOptions' />
        <slider
          ref='slider'
          :is-scheduled-show="isScheduledShow"
          @reloadHistory='reloadHistory'
          v-show="isScheduledShow" />
      </div>
    </div>
    <modalSpots ref='modalSpots' />
  </div>
</template>

<script>
import toastr from 'toastr'
import axios from 'axios'
import Vue from 'vue'
import camelCaseKeys from 'camelcase-keys-deep'
import {
  BForm, BFormGroup, BFormInput, BFormSelect, BFormRadio, BFormRadioGroup, BFormCheckbox, BTooltip, BPopover, BButton, BRow, BContainer, BCol
} from 'bootstrap-vue'
import { mapActions, mapMutations, mapState } from 'vuex'
import geogridsHistory from './live/index'
import geogridDetails from './live/show'
import newGeogrid from './live/new'
import geoAreaMap from './map/map'
import tabs from './tabs'
import modalSpots from './map/modal_spots'
import scheduledGeogridsConfigs from './scheduled/index'
import editConfig from './scheduled/edit'
import newConfig from './scheduled/new'
import slider from './scheduled/show/slider'
import config from './scheduled/show/config'
import searchTermsList from './scheduled/show/search_terms_list'
import { DEFAULT_ERROR_MESSAGE } from '../../common/constants'
import { isEmpty, fetchFromLocalStorage } from '../../common/helpers'

Vue.component('BForm', BForm)
Vue.component('BFormGroup', BFormGroup)
Vue.component('BFormInput', BFormInput)
Vue.component('BFormSelect', BFormSelect)
Vue.component('BFormRadio', BFormRadio)
Vue.component('BFormRadioGroup', BFormRadioGroup)
Vue.component('BFormCheckbox', BFormCheckbox)
Vue.component('BTooltip', BTooltip)
Vue.component('BPopover', BPopover)
Vue.component('BButton', BButton)
Vue.component('BRow', BRow)
Vue.component('BContainer', BContainer)
Vue.component('BCol', BCol)

const GEOGRID_ATTRS_PRESETS_MAPPING = [
  'gridSize', 'gridDistanceMeasure', 'gridPointDistance',
  'localLanguageEnabled', 'shouldNotify'
]

const csrf = document.querySelector('meta[name="csrf-token"]')
axios.defaults.headers.common['X-CSRF-Token'] = csrf.getAttribute('content')
function preloadImage(url) {
  const image = new Image()
  return new Promise((resolve, reject) => {
    image.onload = resolve
    image.onerror = reject
    image.src = url
  })
}

export default {
  name: 'GeogridsWidget',
  components: {
    tabs,
    geoAreaMap,
    modalSpots,
    geogridsHistory,
    geogridDetails,
    newGeogrid,
    scheduledGeogridsConfigs,
    editConfig,
    newConfig,
    slider,
    config,
    searchTermsList
  },
  props: {
    // geogrids: Array,
    // totalGeogrids: Number,
    cable: Object,
    newKeyPath: String,
    keysPath: String,
    apiGeogridsPath: String,
    geogridsPath: String,
    apiConfigsPath: String,
    apiTotalConfigsPath: String,
    pointDistances: Object,
    periodicityOptions: Object,
    dayNumberOptions: Array,
    weekNumberOptions: Array,
    placesDataSourceDefaultOptions: Array,
    distanceMeasureOptions: Array,
    defaultPlacesApiKeys: Array,
    gmbLocationsPath: String,
    allLocations: Array,
    restoreGeogridFormState: Boolean
  },
  data() {
    return {
      pendingGeogridId: null
    }
  },
  mounted() {
    this.parseUrlAndGo(true)
    window.onpopstate = () => {
      this.parseUrlAndGo(!!this.$el.__vue__) // eslint-disable-line no-underscore-dangle
    }
    this.setMap('map')
    this.listenGeogridCreatedCable()
    this.listenGeogridImageGeneratedCable()
    this.listenSearchTermGifGeneratedCable()
    window.addEventListener('resize', this.computeMainDivHeight)
  },
  created() {
    this.setGeogridAttrsPresets()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.computeMainDivHeight)
  },
  computed: {
    ...mapState(['config', 'geogrid', 'loader', 'searchTerm', 'pageTab']),
    pageState: {
      get() { return this.$store.state.pageState },
      set(val) { this.$store.commit('pageState', val) }
    },
    config: {
      get() { return this.$store.state.config },
      set(val) { this.$store.commit('config', val) }
    },
    isScheduledShow() {
      return this.pageState === 'scheduled' && this.pageTab === 'show'
    }
  },
  watch: {
    isScheduledShow() {
      this.$nextTick(() => {
        this.computeMainDivHeight()
      })
    }
  },
  methods: {
    ...mapMutations([
      'setMap',
      'setGeogridImage',
      'updateNewGifData']),
    ...mapActions(['parseUrlAndGo', 'goTo']),
    waitGeogrid(geogridId) {
      this.pendingGeogridId = geogridId
    },
    createdNewConfig(createdConfig) {
      this.$refs.history.reload()
      this.config = createdConfig
      this.goTo('show')
    },
    reloadHistory() {
      this.$refs.history.reload()
    },
    listenGeogridCreatedCable() {
      const sub = this.cable.subscriptions.create('PlacesChannel', {
        received: (message) => {
          if (message.type === 'geogrid.finished' || message.type === 'geogrid.error') {
            this.reloadHistory()
          }
        }
      })
      $(document).one('turbolinks:before-visit turbolinks:visit turbolinks:click', () => {
        this.cable.subscriptions.remove(sub)
      })
    },
    updateImageButton(id, url) {
      const histComp = this.$refs.history
      const geogridOnIndex = histComp.$refs?.table?.rows?.find((gr) => gr?.obfuscatedId === id)

      if (geogridOnIndex) {
        geogridOnIndex.imagePublicPath = url
        const dropdownImageBtnComp = histComp.$refs[`live-config-row-${id}`]?.$refs[`dropdownActions-${id}`]?.$refs[`${id}-button-geogrid-get-image`]
        dropdownImageBtnComp.imageGenerating = false
      }
      if (this.geogrid.obfuscatedId === id) this.setGeogridImage(url)
    },
    listenGeogridImageGeneratedCable() {
      const sub = this.cable.subscriptions.create('GenerateGeogridImageChannel', {
        received: ({
          id, type, url, title
        }) => {
          if (type === 'image:generate:success') {
            preloadImage(url).then(() => {
              toastr.success(
                `<a href='${url}' class='geogrid-image-link' target='_blank'>${title}</a>
                 <a href='${url}' class='geogrid-image-preview' target='_blank'><div><img src='${url}'/></div></a>`,
                'Your image is ready',
                { positionClass: 'geogrid-image toast-top-right' }
              )
            }).catch(() => {
              toastr.success(`<a href='${url}' class='geogrid-image-link' target='_blank'>${title}</a>`, 'Your image is ready')
            })
            this.updateImageButton(id, url)
          } else {
            toastr.error(DEFAULT_ERROR_MESSAGE)
          }
        }
      })
      $(document).one('turbolinks:before-visit', () => {
        this.cable.subscriptions.remove(sub)
      })
    },
    // It wont recognize attributes, if they will be CamelCase
    /* eslint-disable camelcase */
    listenSearchTermGifGeneratedCable() {
      const sub = this.cable.subscriptions.create('GenerateGeogridsHistoryAnimationChannel', {
        received: (data) => {
          const {
            url,
            businessName,
            searchTerm,
            configId,
            gifDate,
            status
          } = camelCaseKeys(data)
          if (status) {
            // wait to previous toastr to disapear so positionClass get updated
            setTimeout(() => {
              preloadImage(url).then(() => {
                toastr.success(
                  `<a href='${url}' class='geogrid-image-link' target='_blank'>${businessName}: ${searchTerm}.</a>
                   <a href='${url}' class='geogrid-image-preview' target='_blank'><div><img src='${url}'/></div></a>`,
                  'Your GIF is ready',
                  { positionClass: 'geogrid-image toast-top-right' }
                )
              }).catch(() => {
                toastr.success(
                  `<a href='${url}' class='geogrid-image-link' target='_blank'>${businessName}: ${searchTerm}.</a>`,
                  'Your GIF is ready'
                )
              })
            }, 2000)
            if (this.pageState === 'scheduled' &&
              this.config.obfuscatedId === configId &&
              this.searchTerm.text === searchTerm) {
              this.updateNewGifData(url, gifDate)
            }
          } else {
            toastr.error(`GIF for <u>${businessName}</u> has failed. ${DEFAULT_ERROR_MESSAGE}`, { timeOut: 0 })
          }
        }
      })
      $(document).one('turbolinks:before-visit', () => {
        this.cable.subscriptions.remove(sub)
      })
    },
    /* eslint-enable camelcase */
    setGeogridAttrsPresets() {
      if (!this.restoreGeogridFormState) return
      const attrs = fetchFromLocalStorage(GEOGRID_ATTRS_PRESETS_MAPPING)

      if (isEmpty(attrs)) return

      this.$store.commit('setAttrsPresets', attrs)
    },
    computeMainDivHeight() {
      const bigMapContainer = document.querySelector('.geogrids-widget-container .big-map')
      if (!bigMapContainer) return

      bigMapContainer.style = `min-height: ${window.innerHeight - bigMapContainer.offsetTop - 45}px`
    }
  }
}
</script>
